/**
 * What a confirmed Product Type change (or clear) commits the product to.
 *
 * The screen never deletes anything itself. Confirming records the intent, the save sends it to the
 * server as `productTypeChangeConfirmed`, and the server removes the dependent data before writing the
 * new type — so cancelling the edit, leaving the page, or a failed save leaves the product untouched.
 */
export const PRODUCT_TYPE_CHANGE_CONFIRMED_FLAG = 'productTypeChangeConfirmed';

/** The modules whose data decides whether a Product Type change has to be confirmed. */
export const DEPENDENT_MODULES = ['dam', 'bom', 'purchaseOrders', 'costing', 'samples', 'gradings', 'pim'] as const;

export type DependentModule = (typeof DEPENDENT_MODULES)[number];

/** Which modules hold data for one product, as reported by the server. */
export type DependentDataFlags = Record<DependentModule, boolean>;

/** A product with nothing in any of the modules. */
export const NO_DEPENDENT_DATA: DependentDataFlags = {
  dam: false,
  bom: false,
  purchaseOrders: false,
  costing: false,
  samples: false,
  gradings: false,
  pim: false,
};

/**
 * Whether the change should be confirmed before it is applied. A check that could not be made (`null`)
 * counts as data present, so a failed lookup never lets a destructive change through unannounced.
 * @param flags What the server reported, or null when the lookup failed.
 * @returns True when at least one module holds data, or nothing is known.
 */
export const hasDependentData = (flags: DependentDataFlags | null | undefined): boolean =>
  !flags || DEPENDENT_MODULES.some((module) => flags[module]);

/**
 * Everything a confirmed Product Type change deletes or clears, in the order the confirmation lists it.
 * The list is fixed: it names every area the change can touch, whether or not this product holds data
 * there, so the warning reads the same for every product.
 */
export const PRODUCT_TYPE_CHANGE_ITEM_KEYS = [
  'CATEGORIES',
  'CUSTOM_ATTRIBUTE_VALUES',
  'SUPPLIERS',
  'PRODUCT_TYPE_PROCESSES',
  'BILL_OF_MATERIALS',
  'DIGITAL_ASSETS',
  'MEASUREMENTS_AND_GRADINGS',
  'PURCHASE_ORDERS',
  'PRODUCT_COSTING',
  'SAMPLES',
  'PRODUCT_IDENTIFIER_DATA',
  'ADDITIONAL_VARIABLE_IDENTIFIERS',
  'PIM_ENTRIES',
] as const;
